import { Category } from '@modules/common/types';
import {
  getDataPointId,
  isEmptyWhereClause,
  mapEdgeCategoryToCategory,
} from '@modules/common/utils';
import { AggregateBucketKey, GetAggregatesResponse } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { FieldNames } from 'v2/core/entities/fields';
import useExecuteEQL from 'v2/core/repository/edge-server/eql/useExecuteEQL';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import useQueryInspector from 'v2/core/services/queryBuilder/useQueryInspector';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { useSearchStore } from '../store/searchStore';
import { useSearchAnalysesEqlPropsBy } from './useSearchAnalysesEqlPropsBy';

export function useFetchTrendingReasons() {
  const [reasons, setReasons] = useState<
    { id: string; label: string; value: number; category?: Category }[]
  >([]);

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const eqlProps = useSearchAnalysesEqlPropsBy(FieldNames.Reason);

  const whereClause = useSearchStore(({ query }) => query.whereClause);

  const entityFilters = useQueryInspector(
    whereClause ?? EmptyWhereClause,
    FieldNames.Entity,
  );
  const reasonFilters = useQueryInspector(
    whereClause ?? EmptyWhereClause,
    FieldNames.Reason,
  );

  const shouldNotIncludeIrrelevantReasons =
    !whereClause ||
    isEmptyWhereClause(whereClause) ||
    (entityFilters.length === 0 && reasonFilters.length === 0);

  const resp = useExecuteEQL({
    input: {
      tenantID,
      eqlProps: {
        ...eqlProps,
        functions: [
          {
            name: EQLFunctionName.ApplyTaxonomy,
            params: shouldNotIncludeIrrelevantReasons
              ? undefined
              : ['include-irrelevant'],
          },
        ],
      },
    },
    options: { FetchPolicy: 'cache-and-network' },
  });

  useEffect(() => {
    if (!resp?.data) {
      return;
    }

    const data = resp?.data as GetAggregatesResponse;

    const dataPoints = [];

    for (const { bucket, __typename, ...rest } of data.data) {
      if (dataPoints.length > 4) {
        break;
      }

      if (bucket == null) {
        continue;
      }

      const id = getDataPointId(bucket as AggregateBucketKey);

      dataPoints.push({
        ...rest,
        id,
        label: bucket.label?.displayName ?? '',
        value: rest.value || 0,
        category: mapEdgeCategoryToCategory(bucket.category),
        isVisible: Boolean(bucket.label?.isVisible),
        isUpdateInProgress: Boolean(bucket.label?.updateInProgress),
      });
    }

    setReasons(dataPoints);
  }, [resp?.data]);

  return {
    reasons,
    loading: IsLoading(resp.status) && !resp.data,
    error: resp.error,
    refetch: resp.refetch,
  };
}
